/**
 * Conversations page — /brand/conversations
 *
 * The Marque view, split by a centred toggle into « Tableau de bord » (all the metrics)
 * and « Configurer » (the « Personas » list). A row opens /brand/conversations/:personaId.
 *
 * §4.5.0 — gated behind `conversationsEnabled` flag AND `conversation_personas > 0`.
 * §4.5.1 — shared chrome: period selector, create button.
 * §4.5.2 — Marque view: dashboard = charts + top 10; setup = couverture, pondération,
 *          priorités, personas list.
 *
 * Lots 4+5: analysis status shown in matrix; brand score computed client-side.
 */

import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,

  Select,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TuneIcon from '@mui/icons-material/Tune';
import BarChartIcon from '@mui/icons-material/BarChart';
import SegmentedControl from '../components/common/SegmentedControl';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Flag } from './dashboard/Flag';
import { languageFlag } from './dashboard/geoFlags';
import { api, ApiError } from '../services/api';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import {
  CONVERSATION_INTENTS,
  CONVERSATION_INTENT_ORDER,
  type ConversationPersona,
  type ConversationIntentId,
  type ConversationBrandRollup,
  type ConversationPersonaSummary,
  type ConversationWindow,
} from '@traaker/shared';
import { PromptStudioDialog } from '../components/prompt-studio/PromptStudioDialog';
import { useBrandPersonas } from '../components/conversations/useBrandPersonas';
import ConversationsDashboard from '../components/conversations/ConversationsDashboard';
import { hasLaunchToday } from '../components/conversations/supersede';
import { EngineLogo } from '../components/brand/EngineLogo';
import { BUCKETS, BUCKET_COLOR, bucketMix, scoreColor, type Bucket } from '../components/conversations/view/conversationView';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MixWeights { learn: number; discover: number; compare: number; review: number }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INTENT_COLORS: Record<ConversationIntentId, string> = {
  learn: '#0f7c8c',
  discover: '#c47a12',
  compare: '#b83280',
  review: '#3d64c8',
};

function intentLabel(id: ConversationIntentId, t: (key: string) => string) {
  const map: Record<ConversationIntentId, string> = {
    learn: t('conversations.intentLearn'),
    discover: t('conversations.intentDiscover'),
    compare: t('conversations.intentCompare'),
    review: t('conversations.intentReview'),
  };
  return map[id] ?? id;
}

function statusLabel(status: string, t: (key: string) => string) {
  if (status === 'active') return t('conversations.cardStatusActive');
  if (status === 'paused') return t('conversations.cardStatusPaused');
  if (status === 'archived') return t('conversations.cardStatusArchived');
  return status;
}

function statusColor(status: string): 'success' | 'warning' | 'default' {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  return 'default';
}

// ---------------------------------------------------------------------------
// Persona row — the ⑩ « Personas » panel of the Marque view (§4.5.2)
// ---------------------------------------------------------------------------

/** GET .../launches/:launchId/progress (turn-level) */
interface LaunchProgress {
  launchId: string;
  /** Every cell terminal AND finalized — results readable, launch over. */
  done: boolean;
  /** Turns over, summaries still being written. */
  finalizing: boolean;
  totalTurns: number;
  doneTurns: number;
}

/**
 * Ceiling on `/progress` calls per poll tick. Progress is per launch, so it costs
 * one request per running persona — fine at 3, not at 50 (a brand may hold up to
 * CONVERSATION_MAX_PERSONAS_PER_BRAND, and a cadence tick can make many due at
 * once). Statuses come from a single list read instead, so the cards past this cap
 * still leave `running`; they just show no live turn counter while they work.
 */
const MAX_PROGRESS_POLLS = 6;

/** P · C · G · GA · M — Perplexity, ChatGPT, Gemini, Google AI, Mistral. */
const ENGINE_COLUMNS: { key: string; label: string }[] = [
  { key: 'perplexity', label: 'Perplexity' },
  { key: 'chatgpt', label: 'ChatGPT' },
  { key: 'gemini', label: 'Gemini' },
  { key: 'google', label: 'Google AI' },
  { key: 'mistral', label: 'Mistral' },
];

type PersonaSlice = ConversationPersonaSummary['windows'][ConversationWindow];

/**
 * What the row may honestly claim about this persona.
 *
 * The list is driven by the persona CONFIG rows (which always exist); the `PSUM#`
 * measurement row is an enrichment that only lands once a launch has completed and
 * the rollup has run. A persona without one is never omitted and never shown as 0 %.
 */
type MeasureState = 'measured' | 'noRunsInWindow' | 'measuring' | 'notMeasuredYet';

function measureState(p: ConversationPersona, slice: PersonaSlice | undefined): MeasureState {
  if (slice && slice.n > 0) return 'measured';
  if (slice) return 'noRunsInWindow';
  // nextRunAt alone (scheduled but never launched) must NOT read as "measuring":
  // that misleads a newly created persona into showing "en cours de mesure" with 0 runs.
  if (p.lastLaunchStatus === 'running' || p.lastLaunchAt) return 'measuring';
  return 'notMeasuredYet';
}

/** City name for a city, region name for a region, country name for a country. */
const COUNTRY_NAMES = (() => { try { return new Intl.DisplayNames(['fr'], { type: 'region' }); } catch { return null; } })();
const LANGUAGE_NAMES = (() => { try { return new Intl.DisplayNames(['fr'], { type: 'language' }); } catch { return null; } })();
function zoneShortName(meta: ConversationPersona['cells'][number]['zoneMeta'] | undefined, zone?: string): string {
  if (!zone && !meta) return 'Mondial'; // I1 step 2: Mondial cell
  if (!meta) return zone ?? 'Mondial';
  if (meta.type === 'country') return COUNTRY_NAMES?.of(meta.countryCode) ?? meta.label;
  if (meta.type === 'region') return meta.regionName ?? meta.label;
  return meta.cityName ?? meta.label;
}
function languageName(code: string): string {
  try { return LANGUAGE_NAMES?.of(code) ?? code; } catch { return code; }
}
/** Time-of-day bucket of an `HH:mm` cadence hour: 6–12 matin, 12–18 après-midi, 18–22 soir, else nuit. */
function dayPart(hhmm: string): { Icon: typeof WbSunnyOutlinedIcon; key: string } {
  const h = Number(hhmm.slice(0, 2));
  if (h >= 6 && h < 12) return { Icon: WbTwilightIcon, key: 'conversations.listHourMorning' };
  if (h >= 12 && h < 18) return { Icon: WbSunnyOutlinedIcon, key: 'conversations.listHourAfternoon' };
  if (h >= 18 && h < 22) return { Icon: NightsStayOutlinedIcon, key: 'conversations.listHourEvening' };
  return { Icon: BedtimeOutlinedIcon, key: 'conversations.listHourNight' };
}

interface PersonaRowProps {
  persona: ConversationPersona;
  /** The `PSUM#` row, when the rollup has written one. */
  summary?: ConversationPersonaSummary;
  /** The linked BrandPersona's live name (absent for an unlinked legacy conversation). */
  personaName?: string;
  window: ConversationWindow;
  /** Present while the persona's last launch is in flight. */
  progress?: LaunchProgress | null;
  onEdit: (p: ConversationPersona) => void;
  onDuplicate: (p: ConversationPersona) => void;
  onDelete: (p: ConversationPersona) => void;
}

// memo: a progress tick must repaint only the row whose props changed.
const PersonaRow = memo(function PersonaRow({
  persona, summary, personaName, window: w, progress,
  onEdit, onDuplicate, onDelete,
}: PersonaRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const slice = summary?.windows?.[w];
  const state = measureState(persona, slice);
  const isRunning = persona.lastLaunchStatus === 'running';
  const pctDone = progress && progress.totalTurns > 0 ? Math.round((progress.doneTurns / progress.totalTurns) * 100) : 0;
  const openDetail = () => navigate(`/brand/conversations/${persona.personaId}`);

  /**
   * Days that classified at least one turn, newest last — the columns of the « évolution »
   * chart (the day's prompt-intent mix, `PSUM#.intentSpark`). A day with nothing classified
   * is dropped rather than drawn as an empty slot: absent means "not measured".
   */
  const intentDays = (summary?.intentSpark ?? [])
    .map((m, i) => ({ i, mix: bucketMix(m) }))
    .filter((d): d is { i: number; mix: Record<Bucket, number> } => d.mix !== null);
  const zones = [...new Set(persona.cells.map((c) => zoneShortName(c.zoneMeta, c.zone)))];
  const languages = [...new Set(persona.cells.map((c) => c.language))];
  const configuredEngines = ENGINE_COLUMNS.map((e) => e.key).filter((k) => persona.cells.some((c) => c.engine === k));
  const intentColor = INTENT_COLORS[persona.intentId];


  return (
    <TableRow hover onClick={openDetail} sx={{ cursor: 'pointer' }}>
      {/* Persona — name + psub chips */}
      <TableCell sx={{ maxWidth: 280 }}>
        <Button
          variant="text"
          onClick={(e) => { e.stopPropagation(); openDetail(); }}
          sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600, textAlign: 'left', lineHeight: 1.3, color: intentColor }}
        >
          {/* The intent is the name's colour (same hues as the intent tabs), no pill (2026-10-12). */}
          <Tooltip title={intentLabel(persona.intentId, t)} placement="top-start">
            <span>{persona.name}</span>
          </Tooltip>
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 0.25 }}>
          {persona.status !== 'active' && (
            <Chip
              label={statusLabel(persona.status, t)}
              size="small"
              color={statusColor(persona.status)}
              variant="outlined"
              sx={{ fontSize: '0.625rem', height: 18 }}
            />
          )}
          {state !== 'measured' && persona.nextRunAt && (
            <Chip
              label={t('conversations.personaPendingZone', {
                date: new Date(persona.nextRunAt).toLocaleDateString('fr-FR'),
              })}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.625rem', height: 18 }}
            />
          )}
        </Box>
        {/* Last run date — only when not currently running */}
        {!isRunning && persona.lastLaunchAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('conversations.personaLastRun', {
              date: new Date(persona.lastLaunchAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }),
              time: new Date(persona.lastLaunchAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            })}
          </Typography>
        )}
        {/* Live launch progress (turns) */}
        {isRunning && (
          <Box sx={{ mt: 0.75, maxWidth: 240 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {progress?.finalizing
                ? t('conversations.progressFinalizing')
                : progress && progress.doneTurns > 0
                  ? `${t('conversations.progressTurns', { done: progress.doneTurns, total: progress.totalTurns })} · ${pctDone} %`
                  : t('conversations.progressWaiting')}
            </Typography>
            <LinearProgress
              variant={progress && progress.doneTurns > 0 ? 'determinate' : 'indeterminate'}
              value={pctDone}
              sx={{ height: 4, borderRadius: 2, mt: 0.25 }}
            />
          </Box>
        )}
      </TableCell>

      {/*
        Moteurs — one slot per engine, logo + its presence. `null` is absent, never 0.

        This was five abstract heat-squares (opacity = presence) under the header
        « Par moteur (P · C · G · GA · M) ». On a persona that tracks a subset of
        engines, every slot rendered as the same hatched square, so the column read as
        decorative noise: nothing on screen said WHICH engine a square was, and the
        legend was five initials in a header the eye never returns to. The logo is the
        identifier users already know from Brand Perception, so it carries the column
        without a legend — and the number is printed rather than encoded as opacity,
        because an opacity ramp is not readable to within 20 points anyway.

        The five slots are still FIXED and in the same order on every row: that is what
        makes the column scannable down a list, and it is also how an untracked engine
        stays visible as a gap instead of silently shifting its neighbours.
      */}
      <TableCell>
        {state !== 'measured' && configuredEngines.length > 0 && (
          // Not measured yet: which engines this conversation will ask, without a number.
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {configuredEngines.map((key) => (
              <Tooltip key={key} title={ENGINE_COLUMNS.find((e) => e.key === key)?.label ?? key}>
                <Box sx={{ display: 'flex', cursor: 'default' }}>
                  <EngineLogo engine={key} size={18} />
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
        {state === 'measured' && (
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {ENGINE_COLUMNS.map(({ key, label }) => {
              // `byEngine[key]` is a `{ n, presence }` pair, not a bare number — an
              // absent key means the engine is not tracked, `presence: null` means
              // tracked with nothing measured. Both read as "untracked" here.
              const ep = slice?.byEngine?.[key]?.presence;
              const measured = ep !== null && ep !== undefined;
              return (
                <Tooltip
                  key={key}
                  title={measured ? `${label} : ${Math.round(ep * 100)} %` : `${label} : ${t('conversations.detailNonSuivi')}`}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.125, cursor: 'default', minWidth: 26 }}>
                    {/* Untracked: desaturated and faded rather than removed — the slot
                        has to stay, and grayscale + opacity reads as "off" without
                        inventing a second icon. */}
                    <Box sx={{ display: 'flex', filter: measured ? 'none' : 'grayscale(1)', opacity: measured ? 1 : 0.3 }}>
                      <EngineLogo engine={key} size={18} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.625rem', lineHeight: 1, fontWeight: 600, color: measured ? 'text.primary' : 'text.disabled' }}
                    >
                      {measured ? `${Math.round(ep * 100)}%` : '—'}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </TableCell>

      {/* Runs — total configured runs per launch (was « Agents » until 2026-10-12) */}
      <TableCell>
        {(() => {
          const totalRuns = persona.cells.reduce((s, c) => s + (c.runs ?? 0), 0);
          return totalRuns > 0 ? (
            <Typography variant="body2">{totalRuns}</Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          );
        })()}
      </TableCell>

      {/* Zone(s) — the city name for a city, the region name for a region, the country
          name for a country (2026-10-12); past two, « +N » with the full list on hover. */}
      <TableCell sx={{ maxWidth: 180 }}>
        {zones.length > 0 && (
          <Tooltip title={zones.join(' · ')}>
            <Typography variant="body2" noWrap sx={{ cursor: 'default' }}>
              {zones.slice(0, 2).join(', ')}{zones.length > 2 ? ` +${zones.length - 2}` : ''}
            </Typography>
          </Tooltip>
        )}
      </TableCell>

      {/* Langue(s) — one flag per language, its name on hover. */}
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {languages.map((l) => (
            <Tooltip key={l} title={languageName(l)}>
              <Box sx={{ cursor: 'default' }}>
                {languageFlag(l) ? <Flag countryCode={languageFlag(l)} size={18} /> : <Typography variant="caption">{l.toUpperCase()}</Typography>}
              </Box>
            </Tooltip>
          ))}
        </Box>
      </TableCell>

      {/* Heure — the cadence tick as a time-of-day icon; exact hour + timezone on hover. */}
      <TableCell>
        {persona.scheduleHour && (() => {
          const { Icon, key } = dayPart(persona.scheduleHour);
          return (
            <Tooltip title={`${t(key)} · ${persona.scheduleHour} (${persona.timezone ?? 'UTC'})`}>
              <Icon sx={{ fontSize: 20, color: 'text.secondary', cursor: 'default', display: 'block' }} aria-label={t(key)} />
            </Tooltip>
          );
        })()}
      </TableCell>

      {/* Persona — the linked BrandPersona (2026-11-02, replaced « Présence »). */}
      <TableCell sx={{ maxWidth: 180 }}>
        {personaName ? (
          <Typography variant="body2" noWrap title={personaName}>{personaName}</Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        )}
      </TableCell>

      {/*
        14 jours — the day's MEAN prompt score (`PSUM#.scoreSpark`, 0..100, 2026-11-02; was the
        daily presence), with gaps for unmeasured days, and the last measured day's score
        printed at the end of the line. An isolated day gets a dot so a one-launch
        conversation does not read as "nothing measured".
      */}
      <TableCell sx={{ minWidth: 120 }}>
        {(() => {
          const spark = summary?.scoreSpark;
          if (!spark) return null;
          const pts: { i: number; v: number }[] = [];
          spark.forEach((v, i) => { if (v !== null) pts.push({ i, v }); });
          if (pts.length === 0) return null;
          const W = Math.max(1, spark.length - 1);
          const toY = (v: number) => Math.max(1, Math.min(27, 28 - (v / 100) * 28));
          const last = Math.round(pts[pts.length - 1].v);
          const line = pts.length === 1 ? (
            <circle cx={W <= 1 ? 0.5 : pts[0].i} cy={toY(pts[0].v)} r={Math.max(0.5, W * 2.5 / 92)} fill={intentColor} />
          ) : (
            <path
              d={pts.map(({ i, v }, k) => `${k === 0 ? 'M' : 'L'}${i},${toY(v).toFixed(1)}`).join('')}
              fill="none"
              stroke={intentColor}
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <svg width="92" height="28" viewBox={`0 0 ${W} 28`} preserveAspectRatio="none">{line}</svg>
              <Typography variant="body2" fontWeight={700} sx={{ color: scoreColor(last).fg }}>{last}</Typography>
            </Box>
          );
        })()}
      </TableCell>

      {/*
        14 jours · évolution — the day's turns by intent (`PSUM#.intentSpark` folded into the
        four buckets, 2026-11-02; was the win / part / lose split). Every column is full
        height: a composition chart, not a volume chart, so a quiet day never reads as a
        small one.
      */}
      <TableCell sx={{ minWidth: 150 }}>
        {intentDays.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 26 }}>
            {intentDays.map(({ i, mix }) => {
              const total = BUCKETS.reduce((n, b) => n + mix[b], 0);
              return (
                <Tooltip
                  key={i}
                  title={BUCKETS.filter((b) => mix[b] > 0)
                    .map((b) => `${t(`conversations.view.bucket_${b}`)} ${Math.round((mix[b] / total) * 100)} %`)
                    .join(' · ')}
                >
                  <Box sx={{ width: 7, height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 0.5, overflow: 'hidden', cursor: 'default' }}>
                    {BUCKETS.map((b) => mix[b] > 0 && <Box key={b} sx={{ flex: mix[b], bgcolor: BUCKET_COLOR[b] }} />)}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </TableCell>

      {/* Actions — clicks here must not open the detail page */}
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={t('conversations.cardEdit')}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); openDetail(); }}>
            {t('conversations.cardViewDetail')}
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onEdit(persona); }}>
            {t('conversations.cardEdit')}
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onDuplicate(persona); }}>
            {t('conversations.cardDuplicate')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setMenuAnchor(null); onDelete(persona); }} sx={{ color: 'error.main' }}>
            {t('conversations.cardDelete')}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
});

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        px: 3,
        textAlign: 'center',
        gap: 2,
      }}
    >
      <ForumOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
      <Typography fontWeight={600} fontSize="1.125rem">
        {t('conversations.noPersonasTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
        {t('conversations.noPersonasBody')}
      </Typography>
      <Button variant="contained" onClick={onCreateClick}>
        {t('conversations.noPersonasCta')}
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Marque view — §4.5.2
// ---------------------------------------------------------------------------

// Use CONVERSATION_INTENT_ORDER so 'review' is included without manually listing it
const INTENT_IDS = CONVERSATION_INTENT_ORDER as unknown as ConversationIntentId[];

/** Compute the brand score for the given mix weights and brand rollup rows.
 *  D39: uniform mean per intent; D37: weighted mix. No minimum persona count:
 *  every intent with a measured presence contributes.
 *  The score is NEVER in any API response — browser only. */
function computeBrandScore(
  rows: ConversationBrandRollup[],
  w: ConversationWindow,
  mix: MixWeights,
): { score: number | null; state: 'global' | 'none' } {
  const rowMap = Object.fromEntries(rows.map(r => [r.intentId, r]));
  const contributions: { presence: number; weight: number }[] = [];

  for (const intentId of INTENT_IDS) {
    const p = rowMap[intentId]?.windows?.[w]?.presence;
    if (p === null || p === undefined) continue;
    contributions.push({ presence: p, weight: mix[intentId] });
  }

  const tw = contributions.reduce((s, c) => s + c.weight, 0);
  if (tw === 0) return { score: null, state: 'none' };
  const score = contributions.reduce((s, c) => s + c.presence * c.weight, 0) / tw;
  return { score, state: 'global' };
}

interface MarqueViewProps {
  brandId: string;
  /** The persona CONFIG list — the panel ⑩ list is driven by this, not by `PSUM#`. */
  personas: ConversationPersona[];
  progressById: Record<string, LaunchProgress>;
  onEdit: (p: ConversationPersona) => void;
  onDuplicate: (p: ConversationPersona) => void;
  onDelete: (p: ConversationPersona) => void;
}

function MarqueView({
  brandId, personas, progressById,
  onEdit, onDuplicate, onDelete,
}: MarqueViewProps) {
  const { t } = useTranslation();


  // Period selector state (client-side toggle, no request)
  const [window, setWindow] = useState<ConversationWindow>('7d');

  const mix: MixWeights = { learn: 60, discover: 30, compare: 10, review: 0 };
  const [top10Order, setTop10Order] = useState<'most' | 'least' | 'up' | 'down'>('most');
  const [personasTabIntent, setPersonasTabIntent] = useState<ConversationIntentId | 'all'>('all');
  const [personasSort, setPersonasSort] = useState<'presence' | 'won' | 'delta'>('presence');
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [tagSearch, setTagSearch] = useState('');

  // Data
  const [brandRows, setBrandRows] = useState<ConversationBrandRollup[]>([]);
  const [personasSummary, setPersonasSummary] = useState<ConversationPersonaSummary[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    Promise.all([
      api.get<any[]>(`/brands/${brandId}/conversations/rollup`),
      api.get<{ items: any[]; nextCursor: string | null }>(`/brands/${brandId}/conversations/personas`),
    ]).then(([rollupItems, personasResp]) => {
      setBrandRows((rollupItems ?? []) as ConversationBrandRollup[]);
      setPersonasSummary((personasResp?.items ?? []) as ConversationPersonaSummary[]);
    }).catch(() => {
      // Show empty states
    }).finally(() => setLoading(false));
  }, [brandId]);

  /** Persona CONFIGS by id — the ⑧ top10 payload carries no `description`, so the
      tooltip reads it off the config list. A top10 entry can name an archived or
      deleted persona, which is simply absent here. */
  const personaById = useMemo(
    () => Object.fromEntries(personas.map(p => [p.personaId, p])),
    [personas],
  );
  // A linked conversation carries no description of its own: it reads its BrandPersona's.
  const { byId: brandPersonaById } = useBrandPersonas(brandId, {
    enabled: personas.some(p => p.brandPersonaId),
  });

  /** `PSUM#` rows, by personaId — an enrichment of the config list, never its source. */
  const summaryById = useMemo(
    () => Object.fromEntries(personasSummary.map(s => [s.personaId, s])),
    [personasSummary],
  );

  // Panel ⑩ list: the configs, filtered by the intent tab and sorted by a measured
  // value when there is one. A persona with no measurement sorts last — never dropped.
  const personaRows = useMemo(() => {
    const value = (p: ConversationPersona): number | null => {
      const slice = summaryById[p.personaId]?.windows?.[window];
      if (!slice || slice.n === 0) return null;
      if (personasSort === 'presence') return slice.presence;
      if (personasSort === 'won') return slice.outcome.win / slice.n;
      const prev = slice.prev?.presence;
      return slice.presence !== null && prev !== null && prev !== undefined ? slice.presence - prev : null;
    };
    return personas
      .filter(p => personasTabIntent === 'all' || p.intentId === personasTabIntent)
      .filter(p => tagFilter.size === 0 || (p.tags ?? []).some(tag => tagFilter.has(tag)))
      .map(p => ({ persona: p, v: value(p) }))
      .sort((a, b) => (b.v ?? -Infinity) - (a.v ?? -Infinity))
      .map(x => x.persona);
  }, [personas, summaryById, window, personasSort, personasTabIntent, tagFilter]);

  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const p of personas) { for (const tag of (p.tags ?? [])) s.add(tag); }
    return [...s].sort();
  }, [personas]);

  // Legacy links still point at `/brand/conversations?tab=personas` (the two tabs are
  // gone). Land them on the persona list instead of breaking, then drop the param.
  const personasPanelRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Which of the two views is showing. Held in the URL, not in `useState`, so a
   * refresh keeps the tab the user was on (2026-10-03).
   *
   * The default is « Configurer » and the param is DELETED rather than set to
   * `setup`, which is what makes the sidebar entry behave: it links to a bare
   * `/brand/conversations`, so arriving from the nav always lands on Configurer
   * whatever the user was looking at before. It used to be
   * `viewChoice ?? (brandRows.length === 0 ? 'setup' : 'dashboard')`, which flipped
   * the landing tab the moment a brand rollup existed.
   *
   * `ScrollToTop` keys on `pathname` only, so toggling the tab does not scroll.
   */
  const storedView = localStorage.getItem('traaker.conversations.view');
  const view: 'dashboard' | 'setup' = searchParams.get('view') === 'dashboard' ? 'dashboard' : storedView === 'dashboard' ? 'dashboard' : 'setup';
  const setView = useCallback((v: 'dashboard' | 'setup') => {
    localStorage.setItem('traaker.conversations.view', v);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (v === 'dashboard') p.set('view', v); else p.delete('view');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const wantsPersonas = searchParams.get('tab') === 'personas';
  useEffect(() => {
    if (!wantsPersonas || loading) return;
    personasPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('tab');
      return p;
    }, { replace: true });
  }, [wantsPersonas, loading, setSearchParams]);

  const { state: scoreState } = useMemo(
    () => computeBrandScore(brandRows, window, mix),
    [brandRows, window, mix],
  );

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('conversations.marqueLoading')}
        </Typography>
      </Box>
    );
  }

  const wLabel = window === '7d' ? t('conversations.period7d') : window === '30d' ? t('conversations.period30d') : t('conversations.period90d');

  return (
    <Box sx={{ bgcolor: '#f8f7fc', minHeight: '100%', mx: -3, px: 3, py: 0.5 }}>
      {/* ① Toolbar: the Brand Perception view switch (same control, labels, icons and
          place as BrandAxisPage — 2026-10-12) + the period selector on the dashboard. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <SegmentedControl
          value={view}
          onChange={setView}
          ariaLabel={t('axisPage.viewSwitchAria')}
          options={[
            { value: 'setup', label: t('common.viewSetup'), icon: <TuneIcon /> },
            { value: 'dashboard', label: t('common.viewDashboard'), icon: <BarChartIcon /> },
          ]}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {view === 'dashboard' && (
            <ToggleButtonGroup
              value={window}
              exclusive
              size="small"
              onChange={(_, v) => v && setWindow(v)}
            >
              <ToggleButton value="7d">{t('conversations.period7d')}</ToggleButton>
              <ToggleButton value="30d">{t('conversations.period30d')}</ToggleButton>
              <ToggleButton value="90d">{t('conversations.period90d')}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {view === 'dashboard' && (
      <>
      {/* ③ Charts dashboard — filter bar (tag/engine/zone) + timeframe + 5 chart panels */}
      {brandId && (
        <ConversationsDashboard
          brandId={brandId}
          brandRows={brandRows}
          personasSummary={personasSummary}
        />
      )}

      {/* ④ Top 10 personas — last block of the dashboard, read after the aggregate
          charts above: raw per-persona presence, with the persona's description on (i). */}
      {brandRows.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography fontWeight={600}>{t('conversations.top10Title')}</Typography>
            <Select
              size="small"
              value={top10Order}
              onChange={(e) => setTop10Order(e.target.value as typeof top10Order)}
              sx={{ ml: 'auto', fontSize: '0.8125rem' }}
            >
              <MenuItem value="most">{t('conversations.top10Most')}</MenuItem>
              <MenuItem value="least">{t('conversations.top10Least')}</MenuItem>
              <MenuItem value="up">{t('conversations.top10Up')}</MenuItem>
              <MenuItem value="down">{t('conversations.top10Down')}</MenuItem>
            </Select>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            {(() => {
              // Merge every intent's top10 list, re-cut at 10.
              // `excludedIntents` is deliberately NOT applied here: its control lives in
              // the Configuration view (« Couverture »), and the dashboard's charts above
              // are server-folded and cannot be filtered by intent client-side. Honouring
              // it in this one block would make the dashboard disagree with itself, with
              // no visible control on screen to explain why.
              const all: Array<{ personaId: string; name: string; value: number; delta: number | null; intentId: ConversationIntentId }> = [];
              for (const row of brandRows) {
                const list = row.windows?.[window]?.top10?.[top10Order] ?? [];
                for (const item of list) {
                  all.push({ ...item, intentId: row.intentId });
                }
              }
              // Sort and dedupe (same personaId can appear in multiple intents)
              const seen = new Set<string>();
              const sorted = all
                .sort((a, b) =>
                  (top10Order === 'most' || top10Order === 'up') ? b.value - a.value : a.value - b.value
                )
                .filter(item => { if (seen.has(item.personaId)) return false; seen.add(item.personaId); return true; })
                .slice(0, 10);

              if (sorted.length === 0) {
                return <Typography variant="body2" color="text.secondary">{t('conversations.personasTableNoMeasured')}</Typography>;
              }

              return sorted.map((item, rank) => (
                <Box
                  key={item.personaId}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: rank < sorted.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                >
                  <Typography variant="caption" color="text.disabled" sx={{ width: 20, textAlign: 'right' }}>
                    {rank + 1}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{item.name}</Typography>
                      {/* No icon at all when the persona is gone (archived/deleted) or has
                          no description — never an icon with an empty tooltip. */}
                      {(() => {
                        const cfg = personaById[item.personaId];
                        const description = (cfg?.brandPersonaId
                          ? brandPersonaById[cfg.brandPersonaId]?.description
                          : cfg?.description)?.trim();
                        if (!description) return null;
                        return (
                          <Tooltip
                            title={description}
                            placement="top"
                            slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                          >
                            <InfoOutlinedIcon
                              aria-label={t('conversations.top10PersonaDescription')}
                              sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }}
                            />
                          </Tooltip>
                        );
                      })()}
                    </Box>
                    <Chip
                      label={CONVERSATION_INTENTS[item.intentId]?.name ?? item.intentId}
                      size="small"
                      sx={{ fontSize: '0.625rem', height: 16, bgcolor: INTENT_COLORS[item.intentId] + '18', color: INTENT_COLORS[item.intentId] }}
                    />
                  </Box>
                  <Box sx={{ width: 100 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.round(item.value * 100))}
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: INTENT_COLORS[item.intentId] } }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ width: 45, textAlign: 'right' }}>
                    {Math.round(item.value * 100)} %
                  </Typography>
                  {item.delta !== null && (
                    <Typography
                      variant="caption"
                      sx={{ width: 50, textAlign: 'right', color: item.delta > 0 ? 'success.main' : item.delta < 0 ? 'error.main' : 'text.secondary' }}
                    >
                      {item.delta > 0 ? '+' : ''}{Math.round(item.delta * 100)} pts
                    </Typography>
                  )}
                </Box>
              ));
            })()}
          </Box>
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              {t('conversations.top10Footnote')}
            </Typography>
          </Box>
        </Paper>
      )}
      </>
      )}

      {view === 'setup' && (
      <>
      {/* ② Coverage warning */}
      {scoreState === 'none' && brandRows.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem', maxWidth: 480 }}>
          <strong>{t('conversations.warnNoScore')}</strong>
        </Alert>
      )}


      {/* ⑧ Personas — the list of the brand's personas; a row opens the persona page.
          Driven by the persona configs, enriched with the `PSUM#` measurement row. */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }} ref={personasPanelRef}>
        <Box sx={{ p: 2, pb: 0, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography fontWeight={600}>{t('conversations.personasTableTitle')}</Typography>
          <Typography variant="caption" color="text.secondary">{wLabel}</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">{t('conversations.personasTableSort')}</Typography>
            <Select
              size="small"
              value={personasSort}
              onChange={(e) => setPersonasSort(e.target.value as typeof personasSort)}
              sx={{ fontSize: '0.8125rem' }}
            >
              <MenuItem value="presence">{t('conversations.personasTableSortPresence')}</MenuItem>
              <MenuItem value="won">{t('conversations.personasTableSortGagne')}</MenuItem>
              <MenuItem value="delta">{t('conversations.personasTableSortEvolution', { w: wLabel })}</MenuItem>
            </Select>
          </Box>
        </Box>
        {/* Intent tabs */}
        <Tabs
          value={personasTabIntent}
          onChange={(_, v) => setPersonasTabIntent(v)}
          sx={{ px: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`${t('conversations.personasTableAllTab')} (${personas.length})`} value="all" />
          {INTENT_IDS.map(intentId => (
            <Tab
              key={intentId}
              label={`${intentLabel(intentId, t)} (${personas.filter(p => p.intentId === intentId).length})`}
              value={intentId}
              sx={{ color: INTENT_COLORS[intentId] }}
            />
          ))}
        </Tabs>
        {allTags.length > 0 && (
          <Box sx={{ px: 2, py: 1 }}>
            {/* Search input — shown only when there are many tags */}
            {allTags.length > 10 && (
              <TextField
                size="small"
                placeholder="Rechercher un tag…"
                value={tagSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagSearch(e.target.value)}
                sx={{ mb: 0.75, width: 220 }}
                inputProps={{ style: { fontSize: '0.78rem', padding: '4px 8px' } }}
              />
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Tags :</Typography>
              {allTags
                .filter(tag => !tagSearch || tag.includes(tagSearch.toLowerCase()))
                .map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => setTagFilter(prev => {
                      const next = new Set(prev);
                      if (next.has(tag)) next.delete(tag); else next.add(tag);
                      return next;
                    })}
                    color={tagFilter.has(tag) ? 'primary' : 'default'}
                    variant={tagFilter.has(tag) ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.6875rem', cursor: 'pointer' }}
                  />
                ))}
              {allTags.length > 10 && !tagSearch && (
                <Typography variant="caption" color="text.disabled">
                  {allTags.length} tags
                </Typography>
              )}
              {tagFilter.size > 0 && (
                <Button size="small" variant="text" sx={{ fontSize: '0.6875rem' }} onClick={() => setTagFilter(new Set())}>
                  Tout afficher
                </Button>
              )}
            </Box>
          </Box>
        )}
        <Divider />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('conversations.listColConversation')}</TableCell>
                <TableCell>{t('conversations.personasTableByEngine')}</TableCell>
                <TableCell>{t('conversations.listColRuns')}</TableCell>
                <TableCell>{t('conversations.listColZones')}</TableCell>
                <TableCell>{t('conversations.listColLanguages')}</TableCell>
                <TableCell>{t('conversations.listColHour')}</TableCell>
                <TableCell>{t('conversations.personasTablePersona')}</TableCell>
                <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{t('conversations.personasTable14j')}<Tooltip title={t('conversations.personasTable14jInfo')} placement="top"><InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></Tooltip></Box></TableCell>
                <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{t('conversations.personasTableResult')}<Tooltip title={t('conversations.personasTableResultInfo')} placement="top"><InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></Tooltip></Box></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {personaRows.map(p => (
                <PersonaRow
                  key={p.personaId}
                  persona={p}
                  summary={summaryById[p.personaId]}
                  personaName={p.brandPersonaId ? brandPersonaById[p.brandPersonaId]?.name : undefined}
                  window={window}
                  progress={progressById[p.personaId] ?? null}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                />
              ))}
              {personaRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('conversations.personasTableNone')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
      </>
      )}

    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Conversations() {
  const { t } = useTranslation();
  const { activeBrand: brand } = useBrand();
  const { tenant } = useAuth();

  const [personas, setPersonas] = useState<ConversationPersona[]>([]);
  const [loading, setLoading] = useState(true);
  // Prompt Studio on its Conversations tab (lot I3): '' (the list), 'new', a personaId (edit), or closed.
  const [studio, setStudio] = useState<string | null>(null);
  // « Cadence »: the popup opens on its list with the footer Conversations popover open.
  const [convFooterOpen, setConvFooterOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // enterprise and agency can run now (D21)
  const tier = tenant?.tier;
  const canRunNow = tier === 'enterprise' || tier === 'agency';

  const fetchPersonas = useCallback(async () => {
    if (!brand?.brandId) return;
    setLoading(true);
    try {
      const result = await api.get<ConversationPersona[]>(`/brands/${brand.brandId}/personas`);
      setPersonas(result ?? []);
    } catch {
      // Silently ignore — will show empty state.
    } finally {
      setLoading(false);
    }
  }, [brand?.brandId]);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  // Turn-level progress for every persona whose last launch is running (5 s poll).
  // Silent by design: no `loading` flip, no list refetch. State is written ONLY
  // when a number moved, and a finished launch swaps just that persona's card.
  const [progressById, setProgressById] = useState<Record<string, LaunchProgress>>({});
  const runningKey = personas
    .filter((p) => p.lastLaunchStatus === 'running' && p.lastLaunchId)
    .map((p) => `${p.personaId}:${p.lastLaunchId}`)
    .sort()
    .join(',');
  useEffect(() => {
    if (!brand?.brandId || !runningKey) return;
    const brandId = brand.brandId;
    const targets = runningKey.split(',').map((k) => {
      const [personaId, launchId] = k.split(':');
      return { personaId, launchId };
    });
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    // ONE list read settles `lastLaunchStatus` for EVERY persona, so statuses cost a
    // single request no matter how many are running. Patch only the cards whose
    // status actually moved — the rest keep their object identity and don't re-render.
    const refreshStatuses = async (doneIds: Set<string>) => {
      try {
        const list = await api.get<ConversationPersona[]>(`/brands/${brandId}/personas`);
        if (cancelled) return;
        setPersonas((prev) => {
          let changed = false;
          const next = prev.map((p) => {
            const fresh = (list ?? []).find((f) => f.personaId === p.personaId);
            if (!fresh) return p;
            // The progress endpoint said done but the rollup has not stamped
            // 'completed' yet: force it locally, or `runningKey` never changes and
            // this effect polls forever.
            const resolved =
              fresh.lastLaunchStatus === 'running' && doneIds.has(p.personaId)
                ? { ...fresh, lastLaunchStatus: 'completed' as const }
                : fresh;
            if (resolved.lastLaunchStatus === p.lastLaunchStatus) return p;
            changed = true;
            return resolved;
          });
          return changed ? next : prev;
        });
      } catch {
        // keep the current cards; the next tick retries
      }
    };

    // A conversation takes minutes, not seconds — a flat 5 s is ~50 wasted
    // round-trips per launch, so only the first minute polls fast.
    const nextDelay = () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed < 60_000) return 5_000;
      if (elapsed < 300_000) return 15_000;
      return 30_000;
    };

    const poll = async () => {
      // Bound the per-persona fan-out. Past the cap a card simply has no live turn
      // counter; refreshStatuses still flips it out of `running` when it finishes.
      const polled = targets.slice(0, MAX_PROGRESS_POLLS);
      const doneIds = new Set<string>();
      await Promise.all(polled.map(async ({ personaId, launchId }) => {
        try {
          const p = await api.get<LaunchProgress>(
            `/brands/${brandId}/personas/${personaId}/launches/${launchId}/progress`,
          );
          if (cancelled) return;
          setProgressById((prev) => {
            const cur = prev[personaId];
            const same = cur && cur.launchId === p.launchId && cur.done === p.done
              && cur.finalizing === p.finalizing
              && cur.doneTurns === p.doneTurns && cur.totalTurns === p.totalTurns;
            return same ? prev : { ...prev, [personaId]: p };
          });
          if (p.done) doneIds.add(personaId);
        } catch {
          // network hiccup — keep polling
        }
      }));
      if (cancelled) return;
      // Fetch statuses when something finished, or unconditionally when personas
      // past the cap have no progress call of their own to report completion.
      if (doneIds.size > 0 || targets.length > MAX_PROGRESS_POLLS) {
        await refreshStatuses(doneIds);
      }
      if (cancelled) return;
      timer = setTimeout(poll, nextDelay());
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [brand?.brandId, runningKey]);

  const handleCreate = () => setStudio('new');

  // Stable handler identities: PersonaCard is memoized, so a progress tick on one
  // card must not hand every other card a new callback.
  const brandIdForHandlers = brand?.brandId;

  const handleEdit = useCallback((p: ConversationPersona) => setStudio(p.personaId), []);

  const handleDuplicate = useCallback(async (p: ConversationPersona) => {
    if (!brandIdForHandlers) return;
    try {
      const result = await api.post<ConversationPersona>(
        `/brands/${brandIdForHandlers}/personas/${p.personaId}/duplicate`,
        {},
      );
      setPersonas((prev) => [result, ...prev]);
      setToast({ message: t('conversations.toastDuplicated'), severity: 'success' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric');
      setToast({ message: msg, severity: 'error' });
    }
  }, [brandIdForHandlers, t]);

  const handleDelete = useCallback(async (p: ConversationPersona) => {
    if (!brandIdForHandlers) return;
    if (!window.confirm(t('conversations.deleteConfirm', { name: p.name }))) return;
    try {
      await api.delete(`/brands/${brandIdForHandlers}/personas/${p.personaId}`);
      setPersonas((prev) => prev.filter((x) => x.personaId !== p.personaId));
      setToast({ message: t('conversations.toastDeleted'), severity: 'success' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric');
      setToast({ message: msg, severity: 'error' });
    }
  }, [brandIdForHandlers, t]);

  /**
   * « Relancer toutes les conversations » (2026-10-12) — replaces the per-row button.
   * Every ACTIVE conversation not already running, launched one after the other (each
   * launch is its own credit reserve + `conversation_run_now` slot, 100/day): a 429
   * stops the loop and the toast says how many are left, rather than firing the rest
   * into the same wall. A single conversation can still be relaunched from its page.
   */
  const runnable = personas.filter((p) => p.status === 'active' && p.lastLaunchStatus !== 'running');
  const [runAllProgress, setRunAllProgress] = useState<{ done: number; total: number } | null>(null);
  const handleRunAll = useCallback(async () => {
    if (!brandIdForHandlers || runnable.length === 0) return;
    const today = runnable.filter((p) => hasLaunchToday(p.lastLaunchAt)).length;
    // D62 — a second launch today replaces today's, without a refund.
    const message = t('conversations.runAllConfirm', { count: runnable.length })
      + (today > 0 ? `\n\n${t('conversations.runAllSupersede', { count: today })}` : '');
    if (!window.confirm(message)) return;
    let launched = 0;
    let failed = 0;
    let limitReset: string | null | undefined;
    setRunAllProgress({ done: 0, total: runnable.length });
    for (const p of runnable) {
      try {
        // One Idempotency-Key per launch (lot I2 8d): a retried request reuses its launchId, never a second reserve.
        const result = await api.post<{ launchId: string }>(
          `/brands/${brandIdForHandlers}/personas/${p.personaId}/run`, {}, { 'Idempotency-Key': crypto.randomUUID() },
        );
        launched++;
        // Flip the row to « en cours » immediately; the poll takes over from here.
        setPersonas((prev) => prev.map((x) => (
          x.personaId === p.personaId ? { ...x, lastLaunchId: result.launchId, lastLaunchStatus: 'running' } : x
        )));
      } catch (err) {
        if (err instanceof ApiError && err.status === 429) {
          // The API sends retryAfterMs (ms to wait), not resetAt.
          limitReset = err.body?.retryAfterMs != null
            ? new Date(Date.now() + err.body.retryAfterMs).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
            : null;
          break;
        }
        failed++;
      }
      setRunAllProgress({ done: launched + failed, total: runnable.length });
    }
    setRunAllProgress(null);
    const left = runnable.length - launched - failed;
    setToast(
      limitReset !== undefined
        ? {
            message: limitReset
              ? t('conversations.runAllRateLimitedReset', { launched, left, time: limitReset })
              : t('conversations.runAllRateLimited', { launched, left }),
            severity: 'error',
          }
        : failed > 0
          ? { message: t('conversations.runAllPartial', { launched, failed }), severity: 'error' }
          : { message: t('conversations.runAllDone', { count: launched }), severity: 'success' },
    );
  }, [brandIdForHandlers, runnable, t]);

  // The popup changed something: the list reloads in the background (no skeleton).
  const refreshPersonas = useCallback(() => {
    if (!brand?.brandId) return;
    api.get<ConversationPersona[]>(`/brands/${brand.brandId}/personas`).then((list) => setPersonas(list ?? []), () => {});
  }, [brand?.brandId]);

  if (!brand) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">{t('conversations.noBrandSelected')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Beta banner */}
      <Alert severity="info" sx={{ mb: 2, fontSize: '0.8125rem' }}>
        {t('conversations.betaBanner')}
      </Alert>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('conversations.pageTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('conversations.pageSubtitle')}
          </Typography>
        </Box>
        {/* Same pair as the axis pages: « Configurer » (the popup's list) then « Nouvel audit » (run all). */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button variant="outlined" startIcon={<ScheduleIcon />} onClick={() => { setConvFooterOpen(true); setStudio(''); }}>
          {t('conversations.cadence')}
        </Button>
        <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setStudio('')}>
          {t('conversations.configure', { count: personas.filter((p) => p.status === 'active').length })}
        </Button>
        <Tooltip
          title={
            !canRunNow ? t('conversations.cardRunNowTooltipTier')
            : runnable.length === 0 ? t('conversations.runAllNone')
            : t('conversations.runAllTooltip', { count: runnable.length })
          }
        >
          <span>
            <Button
              variant="contained"
              startIcon={runAllProgress ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={handleRunAll}
              disabled={!canRunNow || runnable.length === 0 || runAllProgress !== null}
            >
              {runAllProgress
                ? t('conversations.runAllProgress', { done: runAllProgress.done, total: runAllProgress.total })
                : t('conversations.newAudit')}
            </Button>
          </span>
        </Tooltip>
        </Box>
      </Box>

      {/* One brand view; its ⑩ « Personas » panel is the persona list (a row opens one). */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
        </Box>
      ) : personas.length === 0 ? (
        <EmptyState onCreateClick={handleCreate} />
      ) : (
        <MarqueView
          brandId={brand.brandId}
          personas={personas}
          progressById={progressById}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
      )}

      {/* Prompt Studio on its Conversations tab (lot I3, C16) */}
      <PromptStudioDialog
        open={studio !== null}
        tenant={tenant}
        brand={brand}
        initialTab="conversations"
        initialAxis="narrative"
        initialConversation={studio || undefined}
        initialConvFooterOpen={convFooterOpen}
        onClose={() => { setStudio(null); setConvFooterOpen(false); }}
        onChanged={refreshPersonas}
      />

      {/* Toast */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
